import {
  ShiftManagementBulkCopyUnassignedShiftsDocument,
  ShiftManagementCoverageSectionDocument,
  ShiftManagementScheduleBulkCopyDocument,
} from 'generated/resources/graphql'
import uniq from 'lodash/uniq'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { LOGIC_OPERATOR } from 'modules/shift_management/hooks/reducers/filter'
import useChangeView from 'modules/shift_management/hooks/useChangeView'
import { useFilters } from 'modules/shift_management/hooks/useFilters'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useUnassignedShifts } from 'modules/shift_management/hooks/useUnassignedShifts'
import { nameFromHexCode } from 'modules/shift_management/lib/colors'
import { trackAddShiftSubmitEvent } from 'modules/shift_management/lib/trackEvents'
import Shift from 'modules/shift_management/store/internal/models/Shift'
import UnassignedShiftProp from 'modules/shift_management/store/internal/models/UnassignedShift'
import type View from 'modules/shift_management/store/internal/models/View'
import { updateCreatedRestDaysCache } from 'modules/shift_management/utils/apolloCache/updateCreatedRestDaysCache'
import { updateCreatedShiftsCache } from 'modules/shift_management/utils/apolloCache/updateCreatedShiftsCache'

import { WizardData } from './utils'

interface Props {
  data: WizardData
  targetView: View
  onClose: () => void
}

export const useCopyPeriodSchedule = ({ data, targetView, onClose }: Props) => {
  const [{ view }] = useSchedule()
  const { onChangePeriod } = useChangeView()
  const {
    company: { id: currentCompanyId },
  } = useCurrentSession()
  const [{ employeeIds, teamIds, legalEntityIds, customFilters, locationIds }] = useFilters()

  const [bulkCopySchedule] = useMutation(ShiftManagementScheduleBulkCopyDocument, {
    extractorFn: (x) => x.shiftManagementMutations.bulkCopyShiftmanagementSchedule,
    update: (cache, { data: mutationData }) => {
      const schedule =
        mutationData?.shiftManagementMutations.bulkCopyShiftmanagementSchedule.schedule
      const shifts = schedule?.shifts || []
      const restDays = schedule?.restDays || []

      updateCreatedShiftsCache({
        cache,
        view: targetView,
        shifts,
      })

      updateCreatedRestDaysCache({
        cache,
        view: targetView,
        restDays,
      })
    },
  })

  const { running: submitting, performAction: handleScheduleSubmit } = useAsyncAction(
    async () => {
      const employees = employeeIds?.length ? employeeIds : []

      const mutationResult = await bulkCopySchedule({
        variables: {
          companyId: currentCompanyId,
          originalStartAt: data.startDateSchedule,
          targetStartAt: data.destinationStartDate,
          employeeIds: employees,
          logicOperator: LOGIC_OPERATOR.AND,
          customFilters: customFilters,
          daysToCopy: data.daysSchedule,
          includeDraftShifts: true,
          teamIds: teamIds,
          legalEntityIds: legalEntityIds,
          locationIds: locationIds,
        },
      })

      const handled = handleResult(mutationResult, (_errors) => {})

      if (handled.success) {
        const newShifts = handled.data?.schedule?.shifts || []
        const copiedShifts = newShifts.map((shift) =>
          Shift.buildFromGraphQL({
            shift,
            employeeFullName: shift?.employee?.fullName || '',
            employeeAvatar: shift?.employee?.avatar?.url || '',
            employeeLocationId: shift?.employee?.locationId || '0',
          })
        )

        const impactedEmployees: string[] = uniq(copiedShifts.map((s: Shift) => s.employeeId))
        const hexCodes = copiedShifts.map((shift: Shift) => shift.color)
        const uniqHexCodes = uniq(hexCodes)
        const color = uniqHexCodes.map((code) => (code ? nameFromHexCode(code) : null))

        trackAddShiftSubmitEvent({
          view,
          employeeIds: impactedEmployees,
          shiftsCount: copiedShifts.length,
          restDaysCount: handled.data?.schedule?.restDays?.length || 0,
          shiftType: 'copyPeriod',
          color,
          from: 'copy_period',
          withSearch: '',
          advancedOptions: false,
          extras: { days: data.daysSchedule },
        })

        onChangePeriod(targetView)
        onClose()
      }
    },
    {
      successMessage: i18n.t('shift_management.actions.copy_shifts_success'),
      errorFlash: false,
    }
  )

  return {
    submitting,
    handleScheduleSubmit,
  }
}

export const useCopyPeriodCoverage = ({ data, targetView, onClose }: Props) => {
  const [, dispatchSchedule] = useSchedule()
  const [, dispatchUnassignedShifts] = useUnassignedShifts()
  const {
    company: { id: currentCompanyId },
  } = useCurrentSession()

  const [bulkCopyUnassignedShifts, { loading }] = useMutation(
    ShiftManagementBulkCopyUnassignedShiftsDocument,
    {
      extractorFn: (x) => x.shiftManagementMutations.bulkCopyShiftmanagementUnassignedShift,
    }
  )

  const handleCoverageSubmit = async () => {
    const result = await bulkCopyUnassignedShifts({
      variables: {
        companyId: currentCompanyId,
        daysToCopy: data.daysCoverage,
        originalStartAt: data.startDateCoverage,
        targetStartAt: data.destinationStartDate,
      },
      // TODO: Actually update the cache properly
      refetchQueries: [ShiftManagementCoverageSectionDocument],
    })

    const handled = handleResult(result, (_errors) => {})

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('shift_management.actions.copy_unassigned_shifts_success'),
      })

      const values = handled.data?.unassignedShifts || []
      const unassignedShiftsList = values.map((unassignedShift) =>
        UnassignedShiftProp.buildFromGraphQL(unassignedShift)
      )

      dispatchSchedule({ type: 'RESET_VIEW', view: targetView })
      dispatchUnassignedShifts({
        type: 'ADD_UNASSIGNED_SHIFTS',
        unassignedShifts: unassignedShiftsList,
      })

      onClose()
    }
  }

  return {
    submittingCoverage: loading,
    handleCoverageSubmit,
  }
}
